var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { StoreAvailabilityProbabilityEnum } from '@inter-ikea-kompis/enums';
import { SheetAlignmentEnum } from '@inter-ikea-kompis/enums';
import { FontStyle } from '@inter-ikea-kompis/themes';
import { DateFormatter, DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import { AvailabilityUtility } from '@inter-ikea-kompis/utilities';
import { HyperlinkColorEnum } from '@inter-ikea-kompis/component-hyperlink';
import { Hyperlink } from '@inter-ikea-kompis/component-hyperlink';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { Text } from '@ingka/common-styles-webc';
import { registerIcon, store } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { ModalFooter as SkapaModalFooter, ModalHeader as SkapaModalHeader, Prompt as SkapaPrompt, Sheet as SkapaSheet } from '@ingka/modal-webc/passive.js';
import { Status as SkapaStatus } from '@ingka/status-webc/passive.js';
import '@inter-ikea-kompis/component-focus-outline';
import StoreSelectorLayoutEnum from '../../enums/StoreSelectorLayoutEnum.js';
import StoreSelectorModalEnum from '../../enums/StoreSelectorModalEnum.js';
import StoreSelectorTagNameEnum from '../../enums/StoreSelectorTagNameEnum.js';
import StoreSelectorCard from '../store-selector-card/StoreSelectorCard.js';
import StoreSelectorStyle from './StoreSelectorStyle.js';
/**
 * Store Selector enables the user to choose a store in the current market, and displays the stores available for that specific market.
 *
 * @event change Triggered when selected store is changed.
 * @event modalOpen Triggered when a modal should be opened.
 * @event modalClose Triggered when a modal should be closed.
 */
export default class StoreSelector extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Stores.
        this.stores = null;
        // Selected store id.
        this.storeId = null;
        // Layout.
        this.layout = StoreSelectorLayoutEnum.button;
        // Visible modal.
        this.visibleModal = null;
        // Store availability object.
        this.storeAvailabilities = null;
        // Sheet alignment
        this.sheetAlignment = SheetAlignmentEnum.right;
        // Accessibility description that will be set as an "aria-label" attribute on the store selector sheet.
        this.ariaLabel = null;
        this.sheetElement = null;
        this.triggerElement = null;
        this.dexfSettings = null;
    }
    /**
     * Renders the component.
     *
     * @returns Any.
     */
    render() {
        // We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        if (!this.stores || (this.stores && this.stores.length === 0)) {
            return null;
        }
        return html `
			<style>
				${StoreSelectorStyle.getStyle({ theme: this.theme })}
			</style>
			<div class="container">${this.getLayoutElement()}</div>
		`;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.closeSheet();
    }
    async connectedCallback() {
        registerIcon(store);
        this.customElements.define('skapa-status', SkapaStatus);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-sheet', SkapaSheet);
        this.customElements.define('skapa-modal-footer', SkapaModalFooter);
        this.customElements.define('skapa-modal-header', SkapaModalHeader);
        this.customElements.define('skapa-prompt', SkapaPrompt);
        this.customElements.define('kompis-hyperlink', Hyperlink);
        this.customElements.define('kompis-store-selector-card', StoreSelectorCard);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('visibleModal')) {
            if (this.visibleModal === StoreSelectorModalEnum.storeSelectorCard) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    /**
     * Returns a link or button element based on the layout property value.
     *
     * @returns Any.
     */
    getLayoutElement() {
        const isLinkLayout = this.layout === StoreSelectorLayoutEnum.link;
        if (isLinkLayout) {
            return this.getLink();
        }
        return this.getButton();
    }
    /**
     * Returns the button layout element.
     *
     * @returns Any.
     */
    getButton() {
        const store = this.getStore();
        const secondaryVariant = 'secondary';
        return html `
			<skapa-button
				@click="${this.dispatchModalOpenEvent.bind(this)}"
				variant="${secondaryVariant}"
				size="small"
				a11y-label="${this.getStoreAriaLabel(store)}"
			>
				<skapa-icon slot="icon" icon="store"></skapa-icon>
				${store ? store.name : this.getTranslations().storeSelectorTitle}
			</skapa-button>
		`;
    }
    getStoreAriaLabel(store) {
        if (!store?.name) {
            return ''; // use label instead of aria label
        }
        return this.getTranslations()
            .storeSelectorAriaLabelSelectedStore.replace('[[store]]', store.name)
            .replace('{{store}}', store.name);
    }
    /**
     * Returns the link layout element.
     *
     * @returns Any.
     */
    getLink() {
        const storeAvailabilityTranslation = this.getStoreAvailabilityTranslation();
        const labels = [
            this.getAvailabilityLabel(storeAvailabilityTranslation),
            this.getOrderItemCaptionText()
        ];
        if (storeAvailabilityTranslation) {
            labels.push(this.getRestockMessage()); // TODO
        }
        return html `
			<div class="linkContainer ${Text.classnames({ body: { m: true } })}">
				<skapa-icon slot="icon" icon="store"></skapa-icon>
				<div class="storeAvailabilityContainer">${labels}</div>
			</div>
		`;
    }
    /**
     * Will show captionText for StoreSelector when a storeAvailabilties is one item.
     *
     * @returns Any.
     */
    getOrderItemCaptionText() {
        const isOrderItem = this.getStoreAvailabilityStatus() === StoreAvailabilityProbabilityEnum.orderItemSingle;
        if (!isOrderItem) {
            return null;
        }
        return html `<span class="captionText">
			${this.getTranslations().storeAvailabilityOrderItemsCaption}
		</span>`;
    }
    getAvailabilityLabel(storeAvailabilityTranslation) {
        const store = this.getStore();
        const ariaLabel = storeAvailabilityTranslation && store?.name
            ? storeAvailabilityTranslation
                .replace('[[store]]', store.name)
                .replace('{{store}}', store.name)
            : this.getStoreAriaLabel(store);
        const hyperlink = this.getHyperlink(store ? store.name : this.getTranslations().storeSelectorCheckAvailabilityInStore, ariaLabel);
        const storeAvailabilityTranslations = storeAvailabilityTranslation?.includes('{{store}}')
            ? storeAvailabilityTranslation.split('{{store}}')
            : storeAvailabilityTranslation?.split('[[store]]');
        // Since the translations are included in the aria label of the hyperlink, we should aria hide the visual text.
        const prefix = this.wrapInAriaHiddenIfPresent(storeAvailabilityTranslations?.[0]);
        const suffix = this.wrapInAriaHiddenIfPresent(storeAvailabilityTranslations?.[1]);
        const label = html `${prefix}${hyperlink}${suffix}`;
        const statusIndicator = this.getStatusIndicator(label);
        return html `<div class="availabilityLabel">${statusIndicator}</div>`;
    }
    getStatusIndicator(label) {
        const color = this.getStatusIndicatorColor();
        const showStatus = this.getStore() && color;
        return html `
			<skapa-status class="${showStatus ? '' : 'status-hidden'}" colour="${color}" position="inline"
				>${label}</skapa-status
			>
		`;
    }
    wrapInAriaHiddenIfPresent(text, className) {
        return text
            ? html `<span class="${className || ''}" aria-hidden="${true}">${text}</span>`
            : null;
    }
    getHyperlink(label, ariaLabel) {
        return html `<kompis-hyperlink
			@linkClick="${this.dispatchModalOpenEvent.bind(this)}"
			.text="${label}"
			.theme="${this.theme}"
			.color="${HyperlinkColorEnum.grey}"
			.underline="${true}"
			.ariaLabel="${ariaLabel ?? ''}"
		></kompis-hyperlink>`;
    }
    getRestockMessage() {
        // Restock message is only intended for single articles.
        if (this.storeAvailabilities?.length !== 1) {
            return null;
        }
        const storeAvailability = this.storeAvailabilities[0];
        const restock = storeAvailability?.content?.cashCarry?.restock;
        const availabilityStatus = this.storeAvailabilities &&
            AvailabilityUtility.getStoreAvailabilityStatus(this.storeAvailabilities);
        if (availabilityStatus === StoreAvailabilityProbabilityEnum.outOfStock && restock) {
            const restockEarliestDate = DateFormatter.isoStringToDate(restock.earliestDateText);
            const restockLatestDate = DateFormatter.isoStringToDate(restock.latestDateText);
            const formattedRestockEarliestDate = DateFormatter.dateToString(restockEarliestDate, this.getSettings().localisation.dateFormat);
            const formattedRestockLatestDate = DateFormatter.dateToString(restockLatestDate, this.getSettings().localisation.dateFormat);
            const restockMessage = this.getTranslations()
                .storeAvailabilityBackInStockDates.replace('[[dateFrom]]', formattedRestockEarliestDate)
                .replace('{{dateFrom}}', formattedRestockEarliestDate)
                .replace('[[dateTo]]', formattedRestockLatestDate)
                .replace('{{dateTo}}', formattedRestockLatestDate);
            return html `<div class="restock">${restockMessage}</div>`;
        }
        return null;
    }
    getStatusIndicatorColor() {
        const availabilityStatus = this.storeAvailabilities &&
            AvailabilityUtility.getStoreAvailabilityStatus(this.storeAvailabilities);
        return availabilityStatus && AvailabilityUtility.getStoreAvailabilityColor(availabilityStatus);
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.open = false;
            this.sheetElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        const sheetElement = this.createSheetElement();
        this.sheetElement = sheetElement;
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
        sheetElement.open = true;
    }
    createSheetElement() {
        const sheetElement = this.createSheet();
        const sheetHeaderElement = this.createSheetHeader();
        const storeSelectorCardElement = this.createCard();
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(storeSelectorCardElement);
        return sheetElement;
    }
    createSheet() {
        const sheetElement = this.createInternalElement('skapa-sheet');
        const ariaLabel = this.ariaLabel ? this.ariaLabel : this.getTranslations().storeSelectorTitle;
        sheetElement.setAttribute('aria-label', ariaLabel);
        sheetElement.size = 'small';
        sheetElement.fullHeight = true;
        sheetElement.alignToStart = this.sheetAlignment === SheetAlignmentEnum.left;
        sheetElement.style.fontFamily = FontStyle.getFontFamily(this.theme);
        sheetElement.style.zIndex = '500';
        sheetElement.addEventListener('closerequest', () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null,
                        triggerElement: this.triggerElement
                    }
                }));
            }
        });
        sheetElement.addEventListener('closed', () => {
            sheetElement.parentElement?.removeChild(sheetElement);
        });
        return sheetElement;
    }
    createSheetHeader() {
        const sheetHeaderElement = this.createInternalElement('skapa-modal-header');
        sheetHeaderElement.slot = 'header';
        const headerSpan = document.createElement('span');
        headerSpan.slot = 'closebutton-label';
        headerSpan.innerText = this.getTranslations().modalAriaLabelButtonClose;
        sheetHeaderElement.appendChild(headerSpan);
        return sheetHeaderElement;
    }
    createCard() {
        const cardElement = this.createInternalElement('kompis-store-selector-card');
        cardElement.theme = this.theme;
        cardElement.localizedInformation = this.localizedInformation;
        cardElement.translations = this.translations;
        cardElement.stores = this.stores;
        cardElement.storeId = this.storeId;
        cardElement.addEventListener('change', (event) => {
            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                detail: {
                    storeId: event.detail.storeId,
                    visibleModal: null,
                    triggerElement: this.triggerElement
                }
            }));
        });
        return cardElement;
    }
    /**
     * Returns a store from the stores property through the storeId property.
     *
     * @returns IStore or null.
     */
    getStore() {
        return this.stores?.find((store) => store.id === this.storeId) || null;
    }
    getStoreAvailabilityStatus() {
        return (this.storeAvailabilities &&
            AvailabilityUtility.getStoreAvailabilityStatus(this.storeAvailabilities));
    }
    /**
     * Returns the store availability translation.
     *
     * @returns Store availability translation string.
     */
    getStoreAvailabilityTranslation() {
        if (!this.getStore()) {
            return null;
        }
        const status = this.getStoreAvailabilityStatus();
        return AvailabilityUtility.getStoreAvailabilityTranslation(status, this.getTranslations());
    }
    /**
     * Dispatches a modalOpen event.
     *
     * @param event ...
     */
    dispatchModalOpenEvent(event) {
        const triggerElement = this.setTriggerElement(event.target);
        this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: StoreSelectorModalEnum.storeSelectorCard,
                triggerElement
            }
        }));
    }
    setTriggerElement(target) {
        if (target instanceof Element) {
            this.triggerElement = this.getTriggerElement(target);
        }
        return this.triggerElement;
    }
    getTriggerElement(element) {
        if (element.tagName === StoreSelectorTagNameEnum.button) {
            return this.queryButtonElement(element);
        }
        if (element.tagName === StoreSelectorTagNameEnum.hyperlink) {
            return this.queryHyperlinkElement(element);
        }
        return null;
    }
    /**
     * Function for querying the button-element within a kompis-button element.
     *
     * @param element ...
     */
    queryButtonElement(element) {
        const button = element.shadowRoot.querySelector('button');
        return button;
    }
    /**
     * Function for querying the hyperlink element within the composed text layout.
     *
     * @param element ...
     */
    queryHyperlinkElement(element) {
        return (element.shadowRoot
            ?.querySelector(this.customElements.getInternalName('kompis-focus-outline'))
            ?.querySelector('a') ?? null);
    }
}
__decorate([
    property({ type: Object })
], StoreSelector.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], StoreSelector.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], StoreSelector.prototype, "settings", void 0);
__decorate([
    property({ type: Object })
], StoreSelector.prototype, "stores", void 0);
__decorate([
    property({ type: String })
], StoreSelector.prototype, "storeId", void 0);
__decorate([
    property({ type: Object, required: true })
], StoreSelector.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], StoreSelector.prototype, "layout", void 0);
__decorate([
    property({ type: String })
], StoreSelector.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Object })
], StoreSelector.prototype, "storeAvailabilities", void 0);
__decorate([
    property({ type: String })
], StoreSelector.prototype, "sheetAlignment", void 0);
__decorate([
    property({ type: String })
], StoreSelector.prototype, "ariaLabel", void 0);
//# sourceMappingURL=StoreSelector.js.map